/// <reference types="cordova-plugin-network-information" />

import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'

interface NetworkProvider {
  networkState: NetworkState,
  isNetworkConnected: boolean,
  isNetworkLimited: boolean,
  isTestedAgainstEndpoint: boolean,
  validateNetwork: () => Promise<{ reached: boolean, code: string }>
}

export enum NetworkState {
  UNKNOWN = -1,
  ETHERNET = 0,
  WIFI = 1,
  CELL_2G = 2,
  CELL_3G = 3,
  CELL_4G = 4,
  CELL = 5,
  NONE = 100,
  NONE_OVERRIDE = NONE + 1,
}

const NetworkContext = createContext<NetworkProvider>(null!);

export const useNetwork = () => useContext(NetworkContext);

interface NetworkProviderProps {
  endpoint: string,
  alwaysValidate?: boolean,
  timeout?: number,
  children: ReactNode,
}

interface SquidexPingResponse {
  message: string,
  errorCode: string,
  traceId: string,
  type: string,
  details: [string],
  statusCode: number
}


export function NetworkProvider({ endpoint, alwaysValidate = false, timeout = 50000, children }: NetworkProviderProps) {
  // Check if Connection is available (Cordova only)
  const Connection = (window as any).Connection;

  const [networkStates] = useState<{ [key: string]: NetworkState }>(() => {
    const states: { [key: string]: NetworkState } = {};

    // If Connection doesn't exist (web environment), use default values
    if (!Connection) {
      states['unknown'] = NetworkState.UNKNOWN;
      states['ethernet'] = NetworkState.ETHERNET;
      states['wifi'] = NetworkState.WIFI;
      states['2g'] = NetworkState.CELL_2G;
      states['3g'] = NetworkState.CELL_3G;
      states['4g'] = NetworkState.CELL_4G;
      states['cellular'] = NetworkState.CELL;
      states['none'] = NetworkState.NONE;
    } else {
      states[Connection.UNKNOWN] = NetworkState.UNKNOWN;
      states[Connection.ETHERNET] = NetworkState.ETHERNET;
      states[Connection.WIFI] = NetworkState.WIFI;
      states[Connection.CELL_2G] = NetworkState.CELL_2G;
      states[Connection.CELL_3G] = NetworkState.CELL_3G;
      states[Connection.CELL_4G] = NetworkState.CELL_4G;
      states[Connection.CELL] = NetworkState.CELL;
      states[Connection.NONE] = NetworkState.NONE;
    }

    return states;
  });

  const [networkState, setNetworkState] = useState<NetworkState>(() => {
    const connection = (navigator as any).connection;
    return connection?.type ? networkStates[connection.type] ?? NetworkState.UNKNOWN : NetworkState.UNKNOWN;
  });
  const [testedAgainstEndpoint, setTestedAgainstEndpoint] = useState(false)
  const abortSignal = useRef<AbortSignal | null>(null);
  const request = useRef<Promise<{ reached: boolean, code: string }> | null>(null);

  const isAbortError = (error: any) => {
    if (error) {
      return (error.name && error.name === "AbortError") ||
        (error.name && error.name === "TimeoutError") ||
        (error.code && error.code === 20) ||
        error === "Aborted by user"
    }
    return false;
  }

  const validateNetwork = () => {
    if (!abortSignal.current || abortSignal.current.aborted || !request.current) {
      abortSignal.current = AbortSignal.timeout(timeout);
      const connection = (navigator as any).connection;
      let currentState = connection?.type ? networkStates[connection.type] ?? NetworkState.UNKNOWN : NetworkState.UNKNOWN;
      try {
        request.current = fetch(endpoint, { method: "GET", signal: abortSignal.current })
          .then(response => {
            if (response.ok) {
              return { reached: true, code: "" }
            } else {
              return response.json().then((response: SquidexPingResponse) => {
                return { reached: response.statusCode == 204, code: response.message }
              });
            }
          })
          .then(({ reached, code }) => {
            if (reached && currentState > NetworkState.CELL) {
              currentState = NetworkState.UNKNOWN
            } else if (!reached && currentState < NetworkState.NONE) {
              currentState = NetworkState.NONE_OVERRIDE
            }
            setNetworkState(currentState)
            return { reached, code };
          })
          .catch((error: any) => {
            if (currentState < NetworkState.NONE) {
              currentState = NetworkState.NONE_OVERRIDE
            }
            setNetworkState(currentState)
            let code = ""
            if (error instanceof DOMException) {
              code += `: ${error}`;
            }
            return { reached: false, code: code };
          }).finally(() => {
            abortSignal.current = null;
            request.current = null;
            setTestedAgainstEndpoint(true);
          })
      } catch (e) {
        return Promise.resolve({ reached: false, code: "RequestFailed" })
      }
    }
    return request.current;
  }

  const updateNetworkStatus = (online: boolean) => {
    const connection = (navigator as any).connection;
    let currentState = connection?.type ? networkStates[connection.type] ?? NetworkState.UNKNOWN : NetworkState.UNKNOWN;
    if (!connection || networkStates[connection.type] === NetworkState.UNKNOWN) {
      currentState = online ? NetworkState.WIFI : NetworkState.NONE;
    }

    if (currentState === NetworkState.NONE_OVERRIDE || alwaysValidate) {
      validateNetwork();
    } else {
      setNetworkState(currentState)
    }
  }

  useEffect(() => {
    const onOnline = () => updateNetworkStatus(true)
    const onOffline = () => updateNetworkStatus(false)

    document.addEventListener("online", onOnline, false);
    document.addEventListener("offline", onOffline, false);

    validateNetwork();
    return () => {
      document.removeEventListener("online", onOnline, false);
      document.removeEventListener("offline", onOffline, false);
    }
  }, []);

  return (<NetworkContext.Provider
    value={{
      networkState: networkState,
      isTestedAgainstEndpoint: testedAgainstEndpoint,
      isNetworkConnected: networkState !== NetworkState.NONE,
      isNetworkLimited: networkState >= NetworkState.ETHERNET && networkState <= NetworkState.WIFI,
      validateNetwork: validateNetwork
    }}>
    {children}
  </NetworkContext.Provider>)
}